import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type {
  OrganizationDomainTeardownInvalidSelection,
  OrganizationDomainTeardownRequest,
  OrganizationDomainTeardownResult,
} from '../types/models/OrganizationDomainResources';
import type { ApiResponseOrganizationDomainTeardown } from '../types/api/ApiResponseOrganizationDomain';

/**
 * Outcome of one teardown request, as the wizard needs to branch on it:
 *  - `executed`: the backend ran the selection (fully or partially — see `teardown.result`);
 *  - `rejected`: the backend answered a JSend error before or during the run (pre-flight blockers,
 *    invalid ids, mismatched confirmation name, kill switch off) — nothing beyond what `message`
 *    says was done;
 *  - `failed`: no usable answer (network error, non-JSON proxy page).
 */
export type TeardownRunOutcome =
  | { kind: 'executed'; teardown: OrganizationDomainTeardownResult }
  | {
      kind: 'rejected';
      message: string;
      blockedBy: string[] | null;
      invalid: OrganizationDomainTeardownInvalidSelection[] | null;
    }
  | { kind: 'failed'; message: string };

interface UseOrganizationDomainTeardownReturn {
  run: (organizationDomainId: number, body: OrganizationDomainTeardownRequest) => Promise<TeardownRunOutcome>;
  running: boolean;
}

/**
 * Runs the selection-driven deletion (POST /organization/domains/{id}/teardown).
 *
 * JSend contract: success is `body.status === 'success'` with the `teardown` data key; a JSend error
 * carries an optional `data` with `blocked_by` or `invalid`. `response.ok` is never consulted.
 */
export const useOrganizationDomainTeardown = (): UseOrganizationDomainTeardownReturn => {
  const apiFetch = useApiFetch();
  const [running, setRunning] = useState(false);

  const run = useCallback(
    async (organizationDomainId: number, body: OrganizationDomainTeardownRequest): Promise<TeardownRunOutcome> => {
      setRunning(true);
      try {
        const response = await apiFetch(`/organization/domains/${organizationDomainId}/teardown`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        const parsed = (await response.json().catch(() => null)) as ApiResponseOrganizationDomainTeardown | null;

        if (parsed?.status === 'success' && parsed.data !== undefined && 'teardown' in parsed.data) {
          return { kind: 'executed', teardown: parsed.data.teardown };
        }

        const statusText = response.statusText ? ` ${response.statusText}` : '';
        const message = parsed?.message ?? `The deletion request failed (HTTP ${response.status}${statusText})`;

        if (parsed !== null && parsed.status !== 'success') {
          const data = parsed.data;
          return {
            kind: 'rejected',
            message,
            blockedBy: data !== undefined && 'blocked_by' in data ? data.blocked_by : null,
            invalid: data !== undefined && 'invalid' in data ? data.invalid : null,
          };
        }

        return { kind: 'failed', message };
      } catch (err) {
        return {
          kind: 'failed',
          message: err instanceof Error ? err.message : 'An error occurred while running the deletion',
        };
      } finally {
        setRunning(false);
      }
    },
    [apiFetch],
  );

  return { run, running };
};
